import { inject, Injectable } from '@angular/core';
import { PatientPageParams } from '@modules/patient/patient-page-params';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Patient } from '@pixacare/domain';
import { FlattenedSequence, FlattenedTelemonitoring } from '@pixacare/dtos';
import { FilterOperator, PaginationDirection, PaginationQuery, stringToBoolean } from '@pixacare/shared';
import { SequenceStorePaginationService } from '@services/sequence-store-pagination.service';
import { TelemonitoringStorePaginationService } from '@services/telemonitoring-store-pagination.service';
import { FilteredSearch } from '@shared/models/filters/filtered-search';
import { PagedCollection } from '@shared/models/pagination/paged-collection';
import { patientPaginationContext } from '@shared/models/pagination/pagination-context.config';
import { selectHasLicenseWriteAccess } from '@shared/store/licenses/licenses.selectors';
import { patientsActions } from '@shared/store/patients/patients.actions';
import { selectIsPatientStateLoaded, selectPatient } from '@shared/store/patients/patients.selectors';
import {
  selectClientCode,
  selectDepartmentId,
  selectPatientId,
  selectQueryParams,
} from '@shared/store/router/router.selectors';
import { sequencesActions } from '@shared/store/sequences/sequences.actions';
import { selectIsSequenceStateLoaded } from '@shared/store/sequences/sequences.selectors';
import { telemonitoringsActions } from '@shared/store/telemonitorings/telemonitorings.actions';
import { combineLatest, filter, first, map, Observable, skipWhile, Subscription, switchMap } from 'rxjs';

@UntilDestroy()
@Injectable()
export class PatientService {
  private readonly store = inject(Store);
  private readonly telemonitoringStorePaginationService = inject(TelemonitoringStorePaginationService);
  private readonly sequencePaginationService = inject(SequenceStorePaginationService);

  private latestParams: PatientPageParams = {
    clientCode: undefined,
    patientId: undefined,
    departmentId: undefined,
    isArchived: undefined,
  };

  private latestFilteredSearch: FilteredSearch = { search: '', filters: [] };
  private patient$: Observable<Patient>;
  private telemonitorings$: Observable<PagedCollection<FlattenedTelemonitoring[]>>;

  private params$: Observable<PatientPageParams> = combineLatest({
    patientId: this.store.select(selectPatientId).pipe(filter(Boolean)),
    clientCode: this.store.select(selectClientCode),
    departmentId: this.store.select(selectDepartmentId),
    isArchived: this.store.select(selectQueryParams).pipe(map((params) => stringToBoolean(params.is_archived))),
  }).pipe(untilDestroyed(this));

  private readonly hasLicenseWriteAccess$ = this.store.select(selectHasLicenseWriteAccess).pipe(untilDestroyed(this));

  private isPatientStateLoaded$ = this.params$.pipe(
    untilDestroyed(this),
    switchMap(({ clientCode }) => this.store.select(selectIsPatientStateLoaded(clientCode))),
  );

  private readonly isSequenceStateLoaded$ = this.store.select(selectIsSequenceStateLoaded).pipe(untilDestroyed(this));

  private sequenceFiltersSubscription: Subscription;

  resetSequences(): void {
    this.store.dispatch(sequencesActions.resetSequencesState());
  }

  cleanupSequenceSubscription(): void {
    this.sequenceFiltersSubscription?.unsubscribe();
  }

  getPatient(): Observable<Patient> {
    if (!this.patient$) {
      this.patient$ = this.params$.pipe(
        switchMap(({ clientCode, patientId }) => this.store.select(selectPatient(clientCode, patientId))),
        untilDestroyed(this),
      );

      combineLatest([this.params$, this.patient$])
        .pipe(first())
        .subscribe(([parameters, patient]) => {
          if (!patient) {
            this.store.dispatch(patientsActions.getPatient(parameters));
          }
        });
    }

    return this.patient$;
  }

  getParams(): Observable<PatientPageParams> {
    return this.params$;
  }

  getHasLicenseWriteAccess(): Observable<boolean> {
    return this.hasLicenseWriteAccess$;
  }

  getTelemonitorings(): Observable<PagedCollection<FlattenedTelemonitoring[]>> {
    if (!this.telemonitorings$) {
      this.store.dispatch(telemonitoringsActions.resetTelemonitoringsState());

      this.params$.pipe(first()).subscribe(({ clientCode, patientId, departmentId }) => {
        const departmentFilters = departmentId
          ? [{ prop: 'department_id', op: FilterOperator.EQUAL, val: departmentId.toString() }]
          : [];
        this.telemonitoringStorePaginationService.load(clientCode, {
          query: new PaginationQuery({
            filter: [
              {
                prop: 'patient_id',
                op: FilterOperator.EQUAL,
                val: patientId.toString(),
              },
              ...departmentFilters,
            ],
          }),
          reset: true,
          direction: PaginationDirection.NEXT,
        });
      });

      this.telemonitorings$ = this.telemonitoringStorePaginationService.select({}).pipe(untilDestroyed(this));
    }

    return this.telemonitorings$;
  }

  getSequences(
    filteredSearch$: Observable<FilteredSearch>,
    reset?: boolean,
  ): Observable<PagedCollection<FlattenedSequence[]>> {
    // unsubscribing from previous filter bar subscription
    if (this.sequenceFiltersSubscription) {
      this.sequenceFiltersSubscription.unsubscribe();
    }
    // setting up new filter bar subscription
    this.sequenceFiltersSubscription = combineLatest([filteredSearch$, this.params$])
      .pipe(
        untilDestroyed(this),
        filter(([{ search, filters }, params]) => {
          const hasParamsChanged = Object.keys(this.latestParams).some(
            (paramKey) => this.latestParams[paramKey] !== params[paramKey],
          );
          this.latestParams = params;

          const hasFilteredSearchChanged =
            this.latestFilteredSearch.search !== search ||
            Object.keys(this.latestFilteredSearch.filters).some(
              (filterKey, index) =>
                this.latestFilteredSearch.filters[index].val.toString() !== filters[index].val.toString(),
            );
          this.latestFilteredSearch = { search, filters };

          return reset || hasParamsChanged || hasFilteredSearchChanged;
        }),
      )
      .subscribe(([{ search, filters }, { clientCode, departmentId }]) => {
        this.resetSequences();

        this.sequencePaginationService.load(clientCode, {
          query: new PaginationQuery({
            orderBy: ['created_on|desc'],
            filter: [
              ...filters,
              ...(departmentId
                ? [{ prop: 'department_id', op: FilterOperator.EQUAL, val: departmentId.toString() }]
                : []),
            ],
            size: patientPaginationContext.countPerPage,
            search,
          }),
          reset: true,
        });
      });

    return combineLatest([this.sequencePaginationService.select(), this.getParams()]).pipe(
      untilDestroyed(this),
      skipWhile(([data, { patientId }]) => data?.data.some((sequence) => sequence.patientInstance?.id !== patientId)),
      map(([data]) => data),
    );
  }

  getIsSequenceStateLoaded(): Observable<boolean> {
    return this.isSequenceStateLoaded$;
  }

  getIsPatientStateLoaded(): Observable<boolean> {
    return this.isPatientStateLoaded$;
  }
}
